/* eslint-disable eqeqeq */
import React, { useRef, useContext, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { FormDialog } from 'components/dialogs/formDialog';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { updateRestrictedResources } from 'api/restaurant';
import { roundDecimal } from 'utils/roundingHelpers';

export const OrderRefundRequestAddForm = ({
  orderId,
  isOpen,
  closeModal,
  onSaved,
  orderPaidAmount,
  tipsPaidAmount,
  orderRefundedAmount,
  tipsRefundedAmount,
}) => {
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const [isSaving, setIsSaving] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundTipsAmount, setRefundTipsAmount] = useState(0);

  const schema = yup.object().shape({
    amount: yup
      .number()
      .min(0)
      .nullable(true)
      // checking self-equality works for NaN, transforming it to null
      .transform((_, val) => (val == Number(val) ? Number(val) : null)),
    tips_amount: yup
      .number()
      .min(0)
      .nullable(true)
      // checking self-equality works for NaN, transforming it to null
      .transform((_, val) => (val == Number(val) ? Number(val) : null)),
  });

  const cancelButtonRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: schema.default(),
    resolver: yupResolver(schema),
  });

  const onFormSubmit = async (data) => {
    setIsSaving(true);

    const sumbitData = {
      orderId,
      amount: roundDecimal(data.amount || 0),
      tipsAmount: roundDecimal(data.tips_amount || 0),
    };

    if (sumbitData.amount <= 0 && sumbitData.tips_amount <= 0) {
      setIsSaving(false);
      return;
    }

    const response = await updateRestrictedResources({
      token: auth.token,
      updateKey: 'refund_create',
      values: sumbitData,
      alertController,
      companyId: auth.company?.company,
    });

    if (response && response.isAcknowledged) {
      alertController.success({ text: 'Refund request created' });
      onSaved();
      closeModal();
    }

    setIsSaving(false);
  };

  const onError = (data) => console.log(data);

  const calculateTotalAvailableAmount = (totalPaid, totalRefunded) => {
    const totalAvailable = roundDecimal((totalPaid || 0) - (totalRefunded || 0));

    if (totalAvailable < 0) {
      return 0;
    }

    return totalAvailable;
  };

  const calculateTotalRefundedAmount = (totalRefunded) => {
    const totalAvailable = roundDecimal(totalRefunded || 0);

    if (totalAvailable < 0) {
      return 0;
    }

    return totalAvailable;
  };

  const totalAvailableOrderToRefund = calculateTotalAvailableAmount(orderPaidAmount, orderRefundedAmount);
  const totalAvailableTipsToRefund = calculateTotalAvailableAmount(tipsPaidAmount, tipsRefundedAmount);

  const totalAlreadyRefundedOrder = calculateTotalRefundedAmount(orderRefundedAmount);
  const totalAlreadyRefundedTips = calculateTotalRefundedAmount(tipsRefundedAmount);

  return (
    <FormDialog
      isOpen={isOpen}
      cancelButtonRef={cancelButtonRef}
      onClose={closeModal}
      title="Refund request"
      buttons={[
        {
          text: 'Request',
          type: 'submit',
          color: 'red',
          disabled: isSaving || (refundAmount <= 0 && refundTipsAmount <= 0),
          loading: isSaving,
          useFormButton: true,
        },
        { text: 'Cancel', onClick: closeModal, color: 'gray', disabled: isSaving },
      ]}
      onSubmit={handleSubmit(onFormSubmit, onError)}
      small
    >
      <div className="grid grid-cols-6 gap-1 text-left">
        <div className="col-span-6">
          <div className="mt-1">
            <TextboxWrapper
              register={register}
              name="amount"
              title="Refund order amount"
              error={errors?.amount ? { message: 'Amount invalid' } : undefined}
              responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
              placeholder="e.g. 10.50"
              onChange={(e) => {
                const newVal = parseFloat(e.target.value) || 0;

                if (newVal > totalAvailableOrderToRefund) {
                  setValue('amount', totalAvailableOrderToRefund);
                  setRefundAmount(totalAvailableOrderToRefund || 0);
                } else {
                  setRefundAmount(newVal || 0);
                }
              }}
            />
          </div>
        </div>
        <div className="col-span-6 pt-2">
          <div className="flex flex-row">
            <div className="text-sm font-medium text-gray-500 pr-1">Available for order refund:</div>
            <div className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {totalAvailableOrderToRefund.toFixed(2)} Eur
            </div>
          </div>
        </div>
        <div className="col-span-6">
          <div className="flex flex-row">
            <div className="text-sm font-medium text-gray-500 pr-1">Already refunded:</div>
            <div className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {totalAlreadyRefundedOrder.toFixed(2)} Eur
            </div>
          </div>
        </div>

        <div className="col-span-6 mt-5">
          <div className="mt-1">
            <TextboxWrapper
              register={register}
              name="tips_amount"
              title="Refund tips amount"
              error={errors?.tips_amount ? { message: 'Amount invalid' } : undefined}
              responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-4"
              placeholder="e.g. 10.50"
              onChange={(e) => {
                const newVal = parseFloat(e.target.value) || 0;

                if (newVal > totalAvailableTipsToRefund) {
                  setValue('tips_amount', totalAvailableTipsToRefund);
                  setRefundTipsAmount(totalAvailableTipsToRefund || 0);
                } else {
                  setRefundTipsAmount(newVal || 0);
                }
              }}
            />
          </div>
        </div>

        <div className="col-span-6 pt-2">
          <div className="flex flex-row">
            <div className="text-sm font-medium text-gray-500 pr-1">Available for tips refund:</div>
            <div className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {totalAvailableTipsToRefund.toFixed(2)} Eur
            </div>
          </div>
        </div>
        <div className="col-span-6">
          <div className="flex flex-row">
            <div className="text-sm font-medium text-gray-500 pr-1">Already refunded:</div>
            <div className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {totalAlreadyRefundedTips.toFixed(2)} Eur
            </div>
          </div>
        </div>
      </div>
    </FormDialog>
  );
};
